import React, { useMemo, useState } from 'react'
import { Dropdown, Nav } from 'react-bootstrap'
import { useNavigate } from 'react-router-dom'
import {
  AsertoNavBar,
  AsertoNavLink,
  Select,
  SelectOption,
  theme,
  useIdentity,
} from '@aserto/console-common'
import { Query, useQueryClient } from '@tanstack/react-query'

import { useConfiguration } from '../../api/internal'
import AsertoLogo from '../../assets/aserto-horizontal-white-text.svg'
import userAvatar from '../../assets/generic-user-avatar.svg'
import { useActiveConfigurationName } from '../../services/ActiveConfiguration'
import { useConfig } from '../../services/ConfigProvider'
import { QueryKeys } from '../../types/local/general'
import { useHomePathFromConfig } from '../../views/ConfigurationsSettings/homepage'
import ConfigurationModal from '../configurations/ConfigurationModal'
import { DropdownMenu, SelectInputContainer, StyledDropdown } from './styles'

export const NavBar = () => {
  const navigate = useNavigate()
  const { data } = useConfiguration()
  const { user, logout } = useIdentity()
  const { configurationName, setConfigurationName } = useActiveConfigurationName()
  const { authenticationType } = useConfig()

  const { homepagePath } = useHomePathFromConfig()
  const queryClient = useQueryClient()
  const configurations = useMemo(() => {
    return data?.configs || []
  }, [data?.configs])
  const configFields = useMemo(() => {
    return data?.configs?.find((e) => e.name === configurationName)
  }, [configurationName, data?.configs])

  const readOnlyConfig = useMemo(() => {
    return data?.readOnly
  }, [data?.readOnly])

  const configs: Array<SelectOption> = useMemo(() => {
    return (
      configurations.map((o) => ({
        label: o.name,
        value: `${o.name}`,
      })) ?? []
    )
  }, [configurations])
  const options = useMemo(() => {
    return [
      {
        options: [
          {
            label: 'Add a configuration',
            value: 'add',
            shouldStopPropagation: true,
            onClick: () => {
              setShowAddAuthorizerModal(true)
            },
          },
          ...(!!configurationName
            ? [
                {
                  label: 'Manage configuration',
                  value: 'manage',
                  shouldStopPropagation: true,
                  onClick: () =>
                    navigate('/ui/settings', {
                      state: {
                        configuration: {
                          authorizerServiceUrl: '',
                          authorizerApiKey: '',
                          directoryServiceUrl: '',
                          directoryApiKey: '',
                          directoryTenantId: '',
                          directoryReaderServiceUrl: '',
                          directoryWriterServiceUrl: '',
                          directoryModelServiceUrl: '',
                        },
                      },
                    }),
                },
              ]
            : []),
        ],
      },
      {
        label: 'Configurations',
        options: configs,
      },
    ]
  }, [configs, configurationName, navigate])
  const [showAddAuthorizerModal, setShowAddAuthorizerModal] = useState(false)
  const AddConfigurationSelect = () => {
    if (readOnlyConfig) {
      return null
    }
    return (
      <SelectInputContainer>
        <Select
          formatGroupLabel={(a) => {
            return !!a?.label ? (
              <div
                style={{
                  alignItems: 'center',
                  borderBottom: '1px #414141 solid',
                  borderTop: '1px #414141 solid',
                  color: theme.grey100,
                  display: 'flex',
                  fontSize: 14,
                  fontWeight: 'bold',
                  height: '100%',
                  minHeight: '36px',
                }}
              >
                {a.label}
              </div>
            ) : undefined
          }}
          modifyCustomStyle={(style) => {
            if (!style.groupHeading) {
              return {}
            }
            return {
              ...style,
              groupHeading: (styles) => {
                return {
                  ...styles,
                  backgroundColor: '#2a2a2a',
                  margin: 0,
                }
              },
            }
          }}
          options={options}
          placeholder="Select configuration"
          style={{
            width: '100%',
            height: 35,
          }}
          value={
            options
              .flatMap((option) => option.options)
              .find((o) => o.value === configurationName) || null
          }
          onChange={(option) => {
            setConfigurationName(option!.label)
            localStorage.removeItem('tenantId')
            queryClient.removeQueries({
              predicate: (query: Query) => {
                return !query.queryKey.includes(QueryKeys.Configurations)
              },
            })
            window.location.href = homepagePath(String(option?.value))
          }}
        />
      </SelectInputContainer>
    )
  }
  return (
    <>
      <ConfigurationModal
        show={showAddAuthorizerModal}
        onHide={() => setShowAddAuthorizerModal(false)}
      />
      <AsertoNavBar
        expand="xl"
        logo={
          <a href={homepagePath(configurationName)}>
            <img alt="aserto" src={AsertoLogo} />
          </a>
        }
        uncollapsableContent={AddConfigurationSelect()}
      >
        <Nav as="ul" className="mr-auto">
          <>
            {configFields?.authorizerServiceUrl && (
              <Nav.Item as="li">
                <AsertoNavLink
                  end="modules"
                  title="Authorizer"
                  to="/ui/authorizer/modules"
                ></AsertoNavLink>
              </Nav.Item>
            )}
            {(configFields?.directoryServiceUrl ||
              configFields?.directoryReaderServiceUrl ||
              configFields?.directoryModelServiceUrl) && (
              <>
                <Nav.Item as="li">
                  <AsertoNavLink
                    end="model"
                    title="Directory"
                    to="/ui/directory/model"
                  ></AsertoNavLink>
                </Nav.Item>
              </>
            )}
          </>
        </Nav>
        {authenticationType?.toLowerCase() === 'apikey' ? (
          <Nav className="ms-auto d-grid gap-2">
            <Nav.Item as="li">
              <Nav.Link onClick={logout}>Sign out</Nav.Link>
            </Nav.Item>
          </Nav>
        ) : (
          user && (
            <Nav className="d-grid gap-2 d-none d-xl-block ms-auto">
              <StyledDropdown>
                <Dropdown.Toggle as={Nav.Link} className="toggle-profile position-relative">
                  <img
                    alt="Profile"
                    className="nav-user-profile rounded-circle"
                    height="41"
                    src={user?.picture || userAvatar}
                    width="41"
                  />
                </Dropdown.Toggle>
                <DropdownMenu className="dropdown-menu-lg-end">
                  <Dropdown.Header>{user?.name}</Dropdown.Header>
                  <Dropdown.Item id="qsLogoutBtn" onClick={logout}>
                    <i className="fa fa-power-off mr-3 mx-1" />
                    &nbsp; &nbsp; Sign out
                  </Dropdown.Item>
                </DropdownMenu>
              </StyledDropdown>
            </Nav>
          )
        )}
      </AsertoNavBar>
    </>
  )
}
